"use client";

import { useEffect, useState } from "react";
import {
  SetupServiceCtaButton,
  type SetupServiceAuthState,
} from "@/components/setup-service/setup-service-cta-button";

interface SetupForYouStickyCtaProps {
  authState: SetupServiceAuthState;
  ctaLabel: string;
  offer: string;
}

const STICKY_CTA_CLASS =
  // tokens-allow-hex: green CTA — Tailwind v4 palette (green-600/700 OKLCH) diverges from these legacy sRGB hexes, so swapping to bg-done would be a visual change (out of scope)
  "flex min-h-[44px] w-full items-center justify-center rounded-lg bg-[#16a34a] px-4 py-3 text-base font-bold text-white transition-colors hover:bg-[#15803d] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-done focus-visible:ring-offset-2";

export function SetupForYouStickyCta({
  authState,
  ctaLabel,
  offer,
}: SetupForYouStickyCtaProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const heroCta = document.getElementById("hero-cta");
    if (!heroCta) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        setVisible(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(heroCta);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={[
        "fixed bottom-0 left-0 right-0 z-40",
        "border-t border-gray-200 bg-white px-4 pb-4 pt-3 shadow-lg",
        "transition-transform duration-300 ease-in-out",
        "md:hidden",
        visible ? "translate-y-0" : "translate-y-full",
      ].join(" ")}
      // inert (not just aria-hidden) so the off-screen CTA is also removed
      // from the tab order — an aria-hidden element must not contain
      // focusable children
      inert={!visible}
    >
      <SetupServiceCtaButton
        location="setup_for_you"
        authState={authState}
        planSelected={null}
        offer={offer}
        adsTracking={{
          variant: "setup-for-you",
          ctaText: ctaLabel,
          pageSlug: "setup-for-you",
          ctaLocation: "sticky-primary",
          eventName: "setup_for_you_cta_click",
          offer,
          value: 249,
          currency: "GBP",
        }}
        ctaLabel={ctaLabel}
        className={STICKY_CTA_CLASS}
      />
      <p className="mt-1.5 text-center text-xs text-gray-500">
        £249 one-off. You review before use.
      </p>
    </div>
  );
}
